import React, { useMemo } from 'react';
import { ISlideShow, IStatus, IStudent } from '@/components/staffer/Scores/GradesTable/types';
import * as Routes from '@/modules/routes';
import { COLOR_LEGEND } from '@/components/staffer/Scores/GradesTable/variables';
import I18n from '@/modules/i18n';
import useSlideShowExerciseResults
  from '../../../hooks/queries/exerciseResults/useSlideShowExerciseResults';
import GradeCell from './index';
import useGradesTableStore from '../../../store/useGradesTableStore';
import useSlideShowQuestionAnswers
  from '../../../hooks/queries/questionAnswers/useSlideShowQuestionAnswers';
import isAwaitingData from '../../../hooks/queries/isAwaitingData';
import { roundScore, lowScoreHighlightColor } from './utils';

interface SlideShowGradeCellProps {
  slideshow: ISlideShow;
  student: IStudent;
}

const SlideShowGradeCell = ({ slideshow, student }: SlideShowGradeCellProps) => {
  const classroom = useGradesTableStore(state => state.classroom);
  const lessonId = useGradesTableStore(state => state.lesson)?.id;
  const programId = useGradesTableStore(state => state.programId);
  const lowScoresSettings = useGradesTableStore(state => state.lowScoresSettings);

  const exerciseResultsQuery = useSlideShowExerciseResults(classroom, slideshow);
  const { data: exerciseResults } = exerciseResultsQuery;
  const questionAnswersQuery = useSlideShowQuestionAnswers(slideshow);
  const { data: slideshowQuestionAnswers } = questionAnswersQuery;

  const isLoading = isAwaitingData(exerciseResultsQuery, questionAnswersQuery);

  const path = useMemo(() => (
    Routes.grade_by_student_shared_question_answers_path(student, {
      program_id: programId,
      lesson_id: lessonId,
      classroom_id: classroom.id,
      slideshow_id: slideshow.id,
    })
  ), [programId, lessonId, classroom]);

  const { slide_group_count: slideGroupCount } = slideshow;
  const hasSlideGroups = slideGroupCount > 0;

  const exerciseResultsForStudent = exerciseResults.filter(exerciseResult => exerciseResult.student_id === student.id);
  const questionAnswersForStudent = slideshowQuestionAnswers.filter(qa => (qa.student_id === student.id));

  const inProgress = exerciseResultsForStudent.length > 0 || questionAnswersForStudent.length > 0;
  const allTurnedIn = exerciseResultsForStudent.some(er => !!er.completed_at);
  const allGroupsAnswered = hasSlideGroups ? exerciseResultsForStudent.length === slideGroupCount
    : exerciseResultsForStudent.length === 1;
  const allScored = exerciseResultsForStudent.every(er => er.score != null)
    && questionAnswersForStudent.every(qa => qa.score != null);
  const hasQuestionsScored = questionAnswersForStudent.some(answer => !!answer.score);

  const slideShowStatus = () => {
    const status: IStatus = {
      color: '',
      text: '-',
    };

    if (exerciseResultsForStudent.length === 0 && !inProgress) return status;

    // If the slide show is turned in and each slide group has an exercise result.
    if (allTurnedIn && allGroupsAnswered) {
      const totalScore = exerciseResultsForStudent.reduce((acc, cur) => {
        if (cur.score) return acc + parseFloat(cur.score);

        return acc;
      }, 0);

      if (allScored) {
        status.color = lowScoreHighlightColor({ score: totalScore, maxScore: slideshow.max_score, lowScoresSettings });
      } else {
        status.color = COLOR_LEGEND.completed;
      }

      if (hasQuestionsScored) {
        status.text = roundScore(totalScore);
      } else {
        status.text = I18n.t('turned_in');
      }
    } else if (inProgress) {
      status.color = COLOR_LEGEND.in_progress;

      if (hasQuestionsScored) {
        status.text = questionAnswersForStudent.reduce((acc, cur) => {
          if (cur.score) return acc + parseFloat(cur.score);

          return acc;
        }, 0);
      } else {
        status.text = I18n.t('in_progress');
      }
    }

    return status;
  };

  const updatedSinceGraded = exerciseResultsForStudent.filter(er => er.updated_since_graded).length > 0;

  return (
    <GradeCell
      id={`slideshow-${slideshow.id}-student-${student.id}-score`}
      isLoading={isLoading}
      status={slideShowStatus()}
      updatedSinceGraded={updatedSinceGraded}
      url={path}
    />
  );
};

export default SlideShowGradeCell;
